import { useEffect, useRef } from 'react'
import nacl from 'tweetnacl'
import { encodeBase64 } from 'tweetnacl-util'
import { api } from '../services/api'
import { getSignalStore } from '../crypto/signal-store'

const MIN_OPK_THRESHOLD = 10
const REPLENISH_BATCH_SIZE = 50
const CHECK_INTERVAL_MS = 5 * 60 * 1000 // Check every 5 minutes

/**
 * Pre-Key Manager Hook
 * Automatically checks and replenishes one-time pre-keys on the server.
 *
 * On login/reconnect:
 * - Fetches /keys/status
 * - If one_time_pre_keys_remaining < 10, generates + uploads 50 new OPKs
 * - Also ensures signed pre-key exists
 */
export function usePreKeyManager(isConnected: boolean) {
  const lastCheckRef = useRef<number>(0)
  const checkingRef = useRef(false)

  useEffect(() => {
    if (!isConnected) return

    const checkAndReplenish = async () => {
      if (checkingRef.current) return
      checkingRef.current = true

      try {
        // Rate limit: don't check more than once per 5 minutes
        const now = Date.now()
        if (now - lastCheckRef.current < CHECK_INTERVAL_MS) return

        lastCheckRef.current = now
        console.log('[PreKeyManager] Checking key status...')

        const status = await api.getKeyStatus()
        console.log('[PreKeyManager] Status:', status)

        if (status.one_time_pre_keys_remaining < MIN_OPK_THRESHOLD) {
          console.log(`[PreKeyManager] Low OPKs (${status.one_time_pre_keys_remaining}), uploading ${REPLENISH_BATCH_SIZE} new keys`)

          const store = await getSignalStore()
          const identityKP = await store.loadIdentityKeyPair()
          if (!identityKP) {
            console.warn('[PreKeyManager] No identity key, skipping')
            return
          }

          // Get next OPK ID
          let startId = store.getNextOPKId()

          // Generate signed pre-key if needed
          const signedPreKeyPair = nacl.box.keyPair()
          const signKeyPair = nacl.sign.keyPair.fromSeed(identityKP.secretKey.slice(0, 32))
          const signature = nacl.sign.detached(signedPreKeyPair.publicKey, signKeyPair.secretKey)

          const signedPreKey = {
            id: 1,
            publicKey: encodeBase64(signedPreKeyPair.publicKey),
            signature: encodeBase64(signature),
          }

          // Save SPK to store
          await store.saveSignedPreKey({
            keyId: 1,
            keyPair: signedPreKeyPair,
            signature,
          })

          // Generate OPKs
          const oneTimePreKeys: { id: number; publicKey: string }[] = []
          for (let i = 0; i < REPLENISH_BATCH_SIZE; i++) {
            const kp = nacl.box.keyPair()
            const keyId = startId + i
            oneTimePreKeys.push({
              id: keyId,
              publicKey: encodeBase64(kp.publicKey),
            })
            // Save private key locally
            await store.saveOneTimePreKey({ keyId, keyPair: kp })
          }

          // Upload to server
          await api.uploadPreKeys(signedPreKey, oneTimePreKeys)

          // Update next ID tracker
          store.setNextOPKId(startId + REPLENISH_BATCH_SIZE)

          console.log(`[PreKeyManager] Uploaded ${REPLENISH_BATCH_SIZE} OPKs (IDs ${startId}-${startId + REPLENISH_BATCH_SIZE - 1})`)
        } else {
          console.log(`[PreKeyManager] OPKs OK (${status.one_time_pre_keys_remaining} remaining)`)
        }
      } catch (error) {
        console.log('[PreKeyManager] Check failed (non-critical):', error)
      } finally {
        checkingRef.current = false
      }
    }

    // Check immediately on connect
    checkAndReplenish()

    // Set up periodic check
    const interval = setInterval(checkAndReplenish, CHECK_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [isConnected])
}
